import { MaterialCommunityIcons, MaterialIcons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { Box, Button, Center, HStack, Icon, Pressable, Text, VStack } from "native-base";
import React from "react";
import { createShimmerPlaceholder } from "react-native-shimmer-placeholder";
import styled from "styled-components/native";

import HomeBackground from "@/assets/svgs/HomeBackgroud";
import CartFloatingButton from "@/components/atoms/CartFloatingButton";
import RoundedIcon from "@/components/atoms/RoundedIcon";
import RoundedLogo from "@/components/atoms/RoundedLogo";
import SearchInput from "@/components/atoms/SearchInput";
import AppStatusBar from "@/components/atoms/StatusBar";
import Container from "@/components/organisms/Container";
import StoreHighlightList from "@/components/organisms/StoreHighlightList";
import { useViewport } from "@/contexts/ViewportContext";
import { useAppNavigation } from "@/hooks/application/navigation";
import { HomeNavigation } from "@/navigation/stacks/HomeStack";
import { formatToCurrency } from "@/utils/helpers";

import { StoreHomeScreenProps } from "./types";

const ShimmerPlaceholder = createShimmerPlaceholder(LinearGradient);

const menuOptions = [
  {
    name: "Promoções",
    icon: <MaterialCommunityIcons name="sale" />,
    path: "promotionProductList",
  },
  {
    name: "Corredores",
    icon: <MaterialCommunityIcons name="basket" />,
    path: "storeHallList",
  },
  {
    name: "Favoritos",
    icon: <MaterialCommunityIcons name="cards-heart" />,
    path: "storeFavoriteProductList",
  },
];

const StoreHomeMobile = ({
  store,
  storeHighlights,
  shipmentValue,
  isLoadingShipmentValue,
  isLoadingStore,
  isLoadingStoreHighlights,
  onOpenProfile,
  onSelectHighlight,
}: StoreHomeScreenProps) => {
  const { navigate, goBack } = useAppNavigation<HomeNavigation>();
  const { width, height } = useViewport();

  return (
    <>
      <Container px="0" pt="0" pb="16">
        <AppStatusBar isSecondaryBg />
        <VStack w="100%" pb="12" bgColor="transparent">
          <Box position="absolute" top="0" left="0" zIndex={-1} bgColor="secondary.400">
            <HomeBackground width={width} height={height} />
          </Box>
          <VStack w="100%" padding="4" zIndex={1} bgColor="transparent">
            <HStack justifyContent="space-between" alignItems="center">
              <Button variant="ghost" onPress={() => goBack()}>
                <Icon as={<MaterialIcons name="arrow-back-ios" />} size="xl" color="primary.600" />
              </Button>
              <Button
                variant="ghost"
                rounded="full"
                bgColor="white.50"
                shadow="4"
                rightIcon={
                  <Icon
                    as={<MaterialIcons name="arrow-forward-ios" />}
                    size="md"
                    color="primary.600"
                  />
                }
                onPress={onOpenProfile}>
                Ver Perfil
              </Button>
            </HStack>
            <Center my="4">
              <RoundedLogo logoUrl={store?.foto_estabelecimento} size="108px" imageSize="lg" />
              <Center my="4">
                {isLoadingStore ? (
                  <LoadingStoreName />
                ) : (
                  <Text fontSize="xl" bold>
                    {store?.nome_fantasia}
                  </Text>
                )}
                {isLoadingShipmentValue ? (
                  <LoadingShipment />
                ) : (
                  <>
                    {typeof shipmentValue === "number" ? (
                      <Text color="primary.600" bold>
                        Frete {shipmentValue === 0 ? "Grátis" : formatToCurrency(shipmentValue)}
                      </Text>
                    ) : (
                      <Text color="error.600">Não atende sua região</Text>
                    )}
                  </>
                )}
                {!!store?.valor_minimo_pedido && (
                  <Text>Pedido mínimo: {formatToCurrency(store?.valor_minimo_pedido)}</Text>
                )}
              </Center>
              <Pressable
                w="85%"
                pointerEvents="box-only"
                onPress={() => navigate("inStoreSearch", { storeId: store?._id })}>
                <SearchInput h="48px" mt="4" fontSize="lg" editable={false} />
              </Pressable>
            </Center>
            <HStack justifyContent="center">
              {menuOptions.map((opt) => (
                <Button
                  key={opt.name}
                  variant="ghost"
                  onPress={() => navigate(opt.path as never, { storeId: store?._id } as never)}>
                  <VStack justifyContent="center" alignItems="center" m="2">
                    <RoundedIcon icon={opt.icon} boxSize="68px" iconSize="36px" />
                    <Text fontSize="md" color={"text.600"} mt="10px" isTruncated>
                      {opt.name}
                    </Text>
                  </VStack>
                </Button>
              ))}
            </HStack>
            {storeHighlights?.length !== 0 && (
              <>
                <Center my="2">
                  <Text fontSize="lg" bold>
                    Destaques
                  </Text>
                </Center>
                <StoreHighlightList
                  data={storeHighlights}
                  isLoading={isLoadingStoreHighlights}
                  onSelect={onSelectHighlight}
                />
              </>
            )}
          </VStack>
        </VStack>
      </Container>
      <CartFloatingButton />
    </>
  );
};

const LoadingStoreName = styled(ShimmerPlaceholder)`
  width: 100%;
  height: 24px;
  margin-top: 8px;
  margin-bottom: 8px;
`;

const LoadingShipment = styled(ShimmerPlaceholder)`
  width: 50%;
  height: 16px;
  margin-top: 8px;
  margin-bottom: 8px;
`;

export default StoreHomeMobile;
